import { PageTypes, PageViews, RowTypes, ScopeTypes } from './constants';
import { pruneEmptyContext } from './contextUtils';
import {
    buildAssignmentContext,
    buildCourseContext,
    buildFeedbackContext,
    buildGradesContext,
    buildGradingInsightsContext,
    buildRubricContext,
    buildStudentContext,
    buildSubmissionContext,
} from './entityContext';
import {
    buildAssignmentVisibleRows,
    buildPeopleVisibleRows,
    buildRubricVisibleRows,
    buildSubmissionVisibleRows,
} from './visibleRows';

const buildAssignmentsPageContext = ({ base, safeAssignments }) =>
    pruneEmptyContext({
        ...base,
        visible_rows: buildAssignmentVisibleRows({ assignments: safeAssignments }),
    });

const buildAssignmentCreatePageContext = ({ assignment, base, safeSubmissions }) =>
    pruneEmptyContext({
        ...base,
        assignment: assignment
            ? buildAssignmentContext({ assignment, includeDescriptionText: true, submissions: safeSubmissions })
            : undefined,
    });

const buildAssignmentOverviewPageContext = ({ assignment, base, safeSubmissions }) =>
    pruneEmptyContext({
        ...base,
        assignment: buildAssignmentContext({ assignment, submissions: safeSubmissions }),
        rubric: buildRubricContext({ rubric: assignment?.rubric, full: false }),
    });

const buildAssignmentEditPageContext = ({ assignment, base, safeSubmissions }) =>
    pruneEmptyContext({
        ...base,
        assignment: buildAssignmentContext({ assignment, includeDescriptionText: true, submissions: safeSubmissions }),
        rubric: buildRubricContext({ rubric: assignment?.rubric, full: true }),
    });

const buildGradingPageContext = ({
    assignment,
    base,
    assignmentSummaryContext,
    safeAccountUsers,
    safeSubmissions,
    selectedSubmission,
}) =>
    pruneEmptyContext({
        ...base,
        assignment: assignmentSummaryContext,
        rubric: buildRubricContext({ rubric: assignment?.rubric, full: true }),
        student: buildStudentContext({ accountUsers: safeAccountUsers, submission: selectedSubmission }),
        submission: buildSubmissionContext({
            accountUsers: safeAccountUsers,
            assignment,
            submission: selectedSubmission,
        }),
        grades: buildGradesContext({ assignment, submission: selectedSubmission }),
        feedback: buildFeedbackContext(selectedSubmission),
        visible_rows: selectedSubmission
            ? undefined
            : buildSubmissionVisibleRows({ accountUsers: safeAccountUsers, assignment, submissions: safeSubmissions }),
    });

const buildAssignmentPreviewPageContext = ({
    assignment,
    base,
    safeAccountUsers,
    safePreviewSubmissions,
    selectedSubmission,
}) =>
    pruneEmptyContext({
        ...base,
        assignment: buildAssignmentContext({
            assignment,
            includeDescriptionText: Boolean(selectedSubmission),
            submissions: safePreviewSubmissions,
        }),
        rubric: buildRubricContext({ rubric: assignment?.rubric, full: Boolean(selectedSubmission) }),
        submission: buildSubmissionContext({
            accountUsers: safeAccountUsers,
            assignment,
            submission: selectedSubmission,
        }),
        grades: buildGradesContext({ assignment, submission: selectedSubmission }),
        feedback: buildFeedbackContext(selectedSubmission),
    });

const buildSubmissionsPageContext = ({
    assignment,
    base,
    assignmentSummaryContext,
    page,
    safeAccountUsers,
    safeSubmissions,
}) => {
    const submissionRows = buildSubmissionVisibleRows({
        accountUsers: safeAccountUsers,
        assignment,
        submissions: safeSubmissions,
    });
    const isViewingInsights = page.view === PageViews.GRADING_INSIGHTS;

    return pruneEmptyContext({
        ...base,
        assignment: assignmentSummaryContext,
        rubric: isViewingInsights ? buildRubricContext({ rubric: assignment?.rubric, full: false }) : undefined,
        insights: isViewingInsights ? buildGradingInsightsContext({ assignment, submissions: safeSubmissions }) : undefined,
        visible_rows: isViewingInsights
            ? {
                  type: RowTypes.GRADES,
                  items: submissionRows.items,
              }
            : submissionRows,
    });
};

const buildSubmissionDetailPageContext = ({
    assignment,
    base,
    assignmentSummaryContext,
    safeAccountUsers,
    selectedSubmission,
}) =>
    pruneEmptyContext({
        ...base,
        assignment: assignmentSummaryContext,
        rubric: buildRubricContext({ rubric: assignment?.rubric, full: false }),
        student: buildStudentContext({ accountUsers: safeAccountUsers, submission: selectedSubmission }),
        submission: buildSubmissionContext({
            accountUsers: safeAccountUsers,
            assignment,
            submission: selectedSubmission,
        }),
        grades: buildGradesContext({ assignment, submission: selectedSubmission }),
        feedback: buildFeedbackContext(selectedSubmission),
    });

const buildPeoplePageContext = ({ base, safeAccountUsers }) =>
    pruneEmptyContext({
        ...base,
        visible_rows: buildPeopleVisibleRows({ accountUsers: safeAccountUsers }),
    });

const buildRubricsPageContext = ({ base, safeRubrics }) =>
    pruneEmptyContext({
        ...base,
        visible_rows: buildRubricVisibleRows({ rubrics: safeRubrics }),
    });

const buildCourseSettingsPageContext = ({ base }) => pruneEmptyContext(base);

const buildFallbackPageContext = ({ page }) =>
    pruneEmptyContext({
        page,
    });

const PageContextBuilders = {
    [PageTypes.ASSIGNMENTS]: buildAssignmentsPageContext,
    [PageTypes.ASSIGNMENT_CREATE]: buildAssignmentCreatePageContext,
    [PageTypes.ASSIGNMENT_OVERVIEW]: buildAssignmentOverviewPageContext,
    [PageTypes.ASSIGNMENT_EDIT]: buildAssignmentEditPageContext,
    [PageTypes.GRADING]: buildGradingPageContext,
    [PageTypes.ASSIGNMENT_PREVIEW]: buildAssignmentPreviewPageContext,
    [PageTypes.SUBMISSIONS]: buildSubmissionsPageContext,
    [PageTypes.SUBMISSION_DETAIL]: buildSubmissionDetailPageContext,
    [PageTypes.PEOPLE]: buildPeoplePageContext,
    [PageTypes.RUBRICS]: buildRubricsPageContext,
    [PageTypes.COURSE_SETTINGS]: buildCourseSettingsPageContext,
};

export const buildInstructorHugoContext = ({
    accountUsers,
    assignments,
    course,
    currentAssignment,
    instructorHugoPageView,
    previewSubmissions,
    routeContext,
    rubrics,
    submissions,
}) => {
    const safeAccountUsers = accountUsers || [];
    const safeAssignments = assignments || [];
    const safePreviewSubmissions = previewSubmissions || [];
    const safeRubrics = rubrics || [];
    const safeSubmissions = submissions || [];
    const assignment = currentAssignment || safeAssignments.find(({ id }) => id === routeContext.assignmentId);
    const selectedSubmission =
        [...safeSubmissions, ...safePreviewSubmissions].find(({ id }) => id === routeContext.detailId) || null;
    const page =
        routeContext.pageType === instructorHugoPageView?.page_type
            ? pruneEmptyContext({ type: routeContext.pageType, view: instructorHugoPageView.view })
            : pruneEmptyContext({ type: routeContext.pageType, view: routeContext.pageView });

    const base = {
        page,
        course: [ScopeTypes.COURSE, ScopeTypes.ASSIGNMENT].includes(routeContext.scopeType)
            ? buildCourseContext({
                  accountUsers: safeAccountUsers,
                  assignments: safeAssignments,
                  course,
                  submissions: safeSubmissions,
              })
            : undefined,
    };

    const assignmentSummaryContext = buildAssignmentContext({
        assignment,
        includeDescriptionText: false,
        includeSettings: false,
        submissions: safeSubmissions,
    });

    const buildPageContext = PageContextBuilders[routeContext.pageType] || buildFallbackPageContext;

    return buildPageContext({
        assignment,
        base,
        assignmentSummaryContext,
        page,
        safeAccountUsers,
        safeAssignments,
        safePreviewSubmissions,
        safeRubrics,
        safeSubmissions,
        selectedSubmission,
    });
};
